import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import React, { useEffect, useState } from 'react';
import { Tooltip, Spin, message, notification } from 'antd';
import { cloneDeep, isEmpty, trim } from 'lodash';
import { IoCloudUploadOutline } from 'react-icons/io5';
import { useDispatch } from 'react-redux';

import Modal from '../../components/uiComponent/modal/index';
import Stepper from '../../components/uiComponent/newStepper';
import Button from '../../components/uiComponent/button/index';
import ReactSelect from '../../components/uiComponent/select';
import FileUpload from '../../components/uiComponent/upload/uploadNew';

import CloseIcon from '../../assets/icons/close-icon-blue.svg';
import ErrorIcon from '../../assets/icons/error-red.svg';
import PartyIcon from '../../assets/icons/party-popper-icon.svg';

import { PRODUCT_SKU_COLUMN_OPTIONS, productSkuTemplateDownloadableFile, fillProductSkuTemplate } from '../../utils/download-file';
import { GetPresignedUrl, StoreProductsFile } from '../../slices/aws-services';
import { GetAllSkuCodes } from '../../slices/3-layer-products';
import { setItem } from '../../slices/auth';

import { AddProductManualModalWrapper, StepDataWrapper } from '../purchase-order/po-details/style';

const STEPS = [
  { title: 'Step 1', description: 'Upload File' },
  { title: 'Step 2', description: 'Data Mapping' },
  { title: 'Step 3', description: 'Review Errors' }
];

const emptyMapping = () =>
  PRODUCT_SKU_COLUMN_OPTIONS.map(({ label, important, optional }) => ({ label, value: '', data: [], important, optional }));

// Combined "create products + map skus" upload as a 4-step wizard (upload -> map columns -> review -> done),
// mirroring the PO "Add by Upload File" flow. Re-serializes the mapped, non-errored rows and uploads them with
// type 'product-sku'; the backend stays the source of truth for the per-row outcome (file-upload-history).
const BulkProductSkuUpload = ({ onSuccess, open: controlledOpen, setOpen: controlledSetOpen, hideTrigger = false, prefillSampleWithSkus = false }) => {
  const dispatch = useDispatch();

  // Supports both self-controlled (Products/Sku pages: own button + state) and externally-controlled
  // (e.g. Business Onboarding step 2 drives `open`/`setOpen` and hides the built-in trigger button).
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledSetOpen : setInternalOpen;
  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState({ rows: [], columns: [] });
  const [fileList, setFileList] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [dataToProceed, setDataToProceed] = useState(emptyMapping());
  const [fileRows, setFileRows] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [sampleDownloading, setSampleDownloading] = useState(false);

  // keep the cell value usable for both display and re-serialization: trim strings, leave numbers intact
  const cell = (value) => (value === undefined || value === null ? '' : typeof value === 'string' ? value.trim() : value);

  // rebuild row objects (keyed by required label) whenever the column mapping changes
  useEffect(() => {
    const length = Math.max(0, ...dataToProceed.map((col) => col.data?.length || 0));
    const rows = Array.from({ length }, (_, index) => {
      const row = {};
      dataToProceed.forEach((col) => {
        row[col.label] = col.data?.[index] ?? '';
      });
      return row;
    });
    setFileRows(rows);
  }, [dataToProceed]);

  const resetState = () => {
    setCurrentStep(0);
    setFileData({ rows: [], columns: [] });
    setFileList([]);
    setFileColumns([]);
    setDataToProceed(emptyMapping());
    setFileRows([]);
    setFileUploading(false);
  };

  const closeModal = () => {
    setOpen(false);
    resetState();
  };

  // "Download Sample File" -> instead of the static blank template, generate the product-sku template
  // pre-filled with the user's unique SKU codes (Business Onboarding). With no SKUs it falls back to a
  // clean template (header + instructions, dummy rows stripped).
  const downloadPrefilledSample = async () => {
    setSampleDownloading(true);
    try {
      const { payload } = await dispatch(GetAllSkuCodes());
      const skus = payload?.success ? payload.result || [] : [];
      await fillProductSkuTemplate(skus);
    } catch (error) {
      notification.error({ message: 'Download Sample', description: 'Could not generate the sample file.', top: 65 });
    } finally {
      setSampleDownloading(false);
    }
  };

  const finishAndRefresh = async () => {
    closeModal();
    if (onSuccess) await onSuccess();
  };

  // Step 0 -> 1: auto-match file columns to required columns by exact header name
  const buildMapping = () => {
    const { rows = [], columns = [] } = fileData;
    setFileColumns(columns.map((col) => ({ label: col, value: col })));
    if (!rows.length) {
      setDataToProceed(emptyMapping());
      return;
    }
    const fileHeaders = Object.keys(rows[0]);
    setDataToProceed(
      PRODUCT_SKU_COLUMN_OPTIONS.map(({ label, important, optional }) => {
        const match = fileHeaders.find((header) => header === label);
        return match
          ? { label, value: label, data: rows.map((row) => cell(row[label])), important, optional }
          : { label, value: '', data: [], important, optional };
      })
    );
  };

  const handleMap = (selectedValue, label) => {
    const mapping = cloneDeep(dataToProceed);
    // a file column can only feed one required column: clear it wherever it was previously used
    const previous = mapping.findIndex((col) => col.value === selectedValue);
    if (previous !== -1) mapping[previous] = { ...mapping[previous], value: '', data: [] };
    const index = mapping.findIndex((col) => col.label === label);
    if (!selectedValue) {
      mapping[index] = { ...mapping[index], value: '', data: [] };
    } else {
      mapping[index] = { ...mapping[index], value: selectedValue, data: (fileData.rows || []).map((row) => cell(row[selectedValue])) };
    }
    setDataToProceed(mapping);
  };

  // Step 1 -> 2: preview validation on the 7 mandatory fields. Errored rows are discarded on proceed.
  const validateRows = () => {
    setFileRows((rows) =>
      rows.map((row) => {
        const { reason, ...rest } = row;
        const val = (label) => `${rest[label] ?? ''}`.trim();
        const packedQty = Number(rest['Packed Qty']);
        const hasCorrectPackedQty = !Number.isNaN(packedQty) && packedQty > 0;

        let nextReason = '';
        if (!val('Sku') && !val('Identifier Value')) nextReason = 'Missing SKU and Identifier';
        else if (!val('Sku')) nextReason = 'Missing SKU';
        else if (!val('Identifier Value')) nextReason = 'Missing Identifier';
        else if (!hasCorrectPackedQty) nextReason = 'Invalid Packed Quantity';
        else if (!val('Identifier Type')) nextReason = 'Missing Identifier Type';
        else if (!val('Variety Pack')) nextReason = 'Missing Variety Pack';
        else if (!val('Product Name')) nextReason = 'Missing Product Name';
        else if (!val('Vendors')) nextReason = 'Missing Vendors';

        return nextReason ? { ...rest, reason: nextReason } : rest;
      })
    );
  };

  const convertToXlsx = (rows) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const binary = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([binary], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  // Step 2 -> submit: re-serialize valid rows and upload as type 'product-sku'
  const submitFile = async () => {
    const validRows = fileRows.filter((row) => !row.reason);
    if (isEmpty(validRows) || isEmpty(fileList[0])) {
      notification.error({ message: 'Upload', description: 'No valid rows to upload' });
      return;
    }
    const extension = fileList[0]?.name?.split('.').pop().toLowerCase();
    const serialized = extension === 'csv' ? Papa.unparse(validRows) : convertToXlsx(validRows);

    setFileUploading(true);
    dispatch(setItem({ field: 'showFileHeader', value: false }));

    let uploadedFile = null;
    await dispatch(GetPresignedUrl({ files: [fileList[0]], fileData: serialized })).then(({ payload }) => {
      if (!payload?.error && payload) uploadedFile = payload[0];
    });

    if (uploadedFile) {
      await dispatch(
        StoreProductsFile({ fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, fileType: uploadedFile?.extension, type: 'product-sku' })
      ).then(({ payload }) => {
        if (!payload?.error) {
          message.success('File Uploaded Successfully');
          setCurrentStep(3);
        } else {
          message.error('Error While Uploading File');
        }
      });
    } else {
      message.error('Error While Uploading File');
    }

    setFileUploading(false);
    setTimeout(() => dispatch(setItem({ field: 'showFileHeader', value: true })), 3000);
  };

  const nextStep = () => {
    if (currentStep === 0) {
      buildMapping();
      setCurrentStep(1);
    } else if (currentStep === 1) {
      validateRows();
      setCurrentStep(2);
    } else if (currentStep === 2) {
      submitFile();
    }
  };

  const nextDisabled = () => {
    if (fileUploading) return true;
    if (currentStep === 0) return !fileList?.length;
    if (currentStep === 1) {
      const mandatory = ['Sku', 'Packed Qty', 'Identifier Value', 'Identifier Type', 'Variety Pack', 'Product Name', 'Vendors'];
      const allMapped = mandatory.every((label) => dataToProceed.find((col) => col.label === label)?.value);
      return !allMapped || !fileRows?.length;
    }
    if (currentStep === 2) return fileRows.every((row) => row.reason);
    return false;
  };

  const handleBack = () => {
    if (currentStep === 2) {
      resetState();
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const erroredRows = fileRows.filter((row) => row.reason);

  return (
    <>
      {!hideTrigger && (
        <Button
          text="Bulk Create & Map SKUs"
          className="primary mb-0"
          startIcon={<IoCloudUploadOutline />}
          minWidth="90px"
          onClick={() => setOpen(true)}
        />
      )}
      <Modal
        open={open}
        onClose={closeModal}
        title={null}
        header={null}
        footer={null}
        className="add-product-flow"
        closeButton={false}
        width={808}
        onHide={null}
        onSave={closeModal}
      >
        <AddProductManualModalWrapper>
          <div className="header-wrapper">
            <span>Create Products &amp; Map SKUs by File Upload</span>
            <img onClick={closeModal} className="cursor-pointer" src={CloseIcon} alt="close" />
          </div>
          <Stepper currentStep={currentStep} steps={STEPS} direction="horizontal" size="default" status="process" />

          <Spin tip={sampleDownloading ? 'Preparing sample file…' : 'Loading..'} spinning={fileUploading || sampleDownloading}>
            <StepDataWrapper>
              {currentStep === 3 ? (
                <div className="congratulations-wrapper">
                  <img src={PartyIcon} alt="success" />
                  <h1>File Uploaded Successfully</h1>
                  <p>Your file is being processed. Track progress and per-row results on the File Upload History page.</p>
                  <Button onClick={finishAndRefresh} text="Done" />
                </div>
              ) : (
                <div className="box-wrapper">
                  <header>
                    <h6>{currentStep === 2 ? 'Review Errors' : 'Product &amp; SKU Information'}</h6>
                  </header>
                  <div className="body-wrapper">
                    {currentStep === 0 && (
                      <FileUpload
                        sampleFileLink={prefillSampleWithSkus ? undefined : productSkuTemplateDownloadableFile}
                        downloadSampleTemplate={prefillSampleWithSkus ? downloadPrefilledSample : undefined}
                        fileList={fileList}
                        setFileData={setFileData}
                        setFileList={setFileList}
                        setDataToProceed={setDataToProceed}
                        setFileColumns={setFileColumns}
                        setFileRows={setFileRows}
                        withoutWarning
                      />
                    )}

                    {currentStep === 1 && (
                      <div className="box-items-wrapper">
                        {dataToProceed.map((item, index) => {
                          const { data = [], value, label, important, optional } = item;
                          return (
                            <div key={index} className="items">
                              <ReactSelect
                                value={value ? { label: value, value } : null}
                                label={label}
                                options={fileColumns}
                                placeholder={label || 'Don’t Import'}
                                className="add-product-manual-wrapper"
                                isClearable
                                important={important}
                                optional={optional}
                                onChange={(selectedOption) => handleMap(selectedOption?.value || '', label)}
                              />
                              <div className="value">
                                {data?.slice(0, 3)?.map((entry, entryIndex) => (
                                  <p key={entryIndex}>{`${entry}`}</p>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {currentStep === 2 && (
                      <div className="box-container">
                        <div className="header-ui-wrapper">
                          <span>Row #</span>
                          <span>SKU</span>
                          <span>Identifier Value</span>
                          <span>Error Message</span>
                        </div>
                        <div className="scroll-able-content new-content">
                          {erroredRows.length === 0 ? (
                            <div className="no-errors-message">
                              <p>No Error Found!.</p>
                            </div>
                          ) : (
                            erroredRows.map((item, index) => (
                              <div key={index} className="body-boxes new-content">
                                <span>{index + 1}</span>
                                <span>{item['Sku'] || '-'}</span>
                                <span>{item['Identifier Value'] || '-'}</span>
                                <Tooltip title={item?.reason || '-'}>
                                  <span className="error-message">
                                    <img src={ErrorIcon} alt="Error" /> {item.reason}
                                  </span>
                                </Tooltip>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </StepDataWrapper>
          </Spin>

          {currentStep === 3 ? null : (
            <footer>
              <Button
                text={currentStep === 2 ? 'Re-Upload File' : 'Back'}
                disabled={currentStep === 0 || fileUploading}
                className="outlined"
                onClick={handleBack}
              />
              <Button
                text={currentStep === 2 ? 'Discard Errors & Proceed' : 'Next'}
                onClick={nextStep}
                disabled={nextDisabled()}
              />
            </footer>
          )}
        </AddProductManualModalWrapper>
      </Modal>
    </>
  );
};

export default BulkProductSkuUpload;
